import styles from "./Modals.module.scss";
import React, { useRef } from "react";
import type { Destination, IncidentTypeValue } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { observer } from "mobx-react-lite";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { IncidentTypesSelect } from "../components/IncidentTypesSelect";
import classNames from "classnames";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { AuditLogNamespaceSelect } from "../components/AuditLogNamespaceSelect";
import omit from "lodash/omit";

const BASE_DATA: Destination = {
  type: "qradar" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
  settings: {
    port: "",
    host: "",
    tls: true,
  },
};

type FormDataType = FormDestinationDataType<"qradar" | "qradarDlpIncident" | "qradarAuditLog"> & {
  namespaces?: string[];
  incidentTypes?: IncidentTypeValue[];
};

export const QradarModalForm: React.FC<DestinationFormProps<"qradar" | "qradarDlpIncident" | "qradarAuditLog">> = observer(
  ({ onSubmitForm, settings, destinationType }) => {
    const { t } = useTranslation();
    const formikContext = useRef<FormikProps<FormDataType>>(null);

    const validate = async (values: FormDataType) => {
      const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
      if (!values.host) errors.host = t("field_is_required");
      if (!values.port) errors.port = t("field_is_required");
      if (destinationType === "qradarDlpIncident" && !values.incidentTypes?.length) {
        errors.incidentTypes = t("field_is_required");
      }
      return errors;
    };

    const getFormValues = () => {
      const data = (formikContext.current?.values ?? {}) as FormDataType;
      if (destinationType === "qradar") {
        return {
          integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
          type: destinationType,
          settings: omit(data, ["namespaces", "incidentTypes"]),
        };
      }
      if (destinationType === "qradarDlpIncident") {
        return {
          integrationType: DESTINATION_INTEGRATION_TYPES.DLP_INCIDENTS,
          type: destinationType,
          settings: omit(data, ["namespaces"]),
        };
      }
      return {
        integrationType: DESTINATION_INTEGRATION_TYPES.AUDIT_LOGS,
        type: destinationType,
        settings: { ...omit(data, ["incidentTypes"]), namespaces: data.namespaces ?? [] },
      };
    };

    const onSubmit = async () => {
      onSubmitForm(getFormValues());
    };

    return (
      <>
        <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
          {destinationType === "qradarAuditLog" && (
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.type")} required />
              <AuditLogNamespaceSelect name="namespaces" />
            </div>
          )}
          {destinationType === "qradarDlpIncident" && <IncidentTypesSelect name="incidentTypes" />}
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.host")} required />
            <FormTextInput name={"host"} paddingInline="none" />
          </div>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.port")} required />
            <FormTextInput name={"port"} paddingInline="none" />
          </div>
          <div className={classNames(styles.field, styles.fieldCheckbox)}>
            <Label className={styles.filedLabel} text={t("destinations_page.secure")} />
            <FormCheckBox name={"tls"} paddingInline="none" />
          </div>
        </FormElement>
        <ModalButtons
          buttonType={"button"}
          testId={"destination-ok-button"}
          submitText={t("destinations_page.save")}
          onSubmit={() => formikContext.current?.submitForm()}
          hideCancel={true}
        />
      </>
    );
  }
);
